import { useTranslation } from 'react-i18next'
import { Aurora, AuroraAssetGroup, AuroraAwhpData, Sensor } from '../types'
import { Card, CardContent, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useMemo, useState } from 'react'
import { Speed, Thermostat } from '@mui/icons-material'
import AuroraAssetGroupHeatPumpStatusControl from './AuroraAssetGroupHeatPumpStatusControl'
import { AuroraCirculationPumpStatus, AuroraHeatPumpStatus } from '../enums'
import AuroraAssetGroupHeatPumpTargetTemperatureControl from './AuroraAssetGroupHeatPumpTargetTemperatureControl'
import AuroraAssetGroupCirculationPumpStatusControl from './AuroraAssetGroupCirculationPumpStatusControl'
import AuroraAssetGroupCirculationPumpModeControl from './AuroraAssetGroupCirculationPumpModeControl'

interface AuroraAssetGroupControlsProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  sensors: Record<string, Sensor>
}

export default function AuroraAssetGroupControls({
  auroraAssetGroup,
  auroraAwhpData,
  sensors
}: AuroraAssetGroupControlsProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme hook.
   */
  const theme = useTheme()

  /**
   * The auroras.
   */
  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  /**
   * Indicates if every aurora has the heat pump on.
   */
  const [isHeatPumpOn, setHeatPumpOn] = useState<boolean>(
    auroras.every((aurora) => aurora.isHeatPumpOn)
  )

  /**
   * Indicates if every aurora has the circulation pump on.
   */
  const [isCirculationPumpOn, setCirculationPumpOn] = useState<boolean>(
    auroras.every((aurora) => aurora.isCirculationPumpOn)
  )

  return (
    <Card>
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={6}>
            <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 4 }}>
              <Thermostat fontSize="large" htmlColor={theme.palette.text.secondary} />
              <Typography variant="h6">{t('heatPumpControl')}</Typography>
            </Stack>

            <Stack direction="column" gap={2}>
              <AuroraAssetGroupHeatPumpStatusControl
                auroraAssetGroup={auroraAssetGroup}
                afterSubmit={(status) => setHeatPumpOn(status === AuroraHeatPumpStatus.ON)}
              />

              <AuroraAssetGroupHeatPumpTargetTemperatureControl
                auroraAssetGroup={auroraAssetGroup}
                auroraAwhpData={auroraAwhpData}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 4 }}>
              <Speed fontSize="large" htmlColor={theme.palette.text.secondary} />
              <Typography variant="h6">{t('circulationPumpControl')}</Typography>
            </Stack>

            <Stack direction="column" gap={2}>
              <AuroraAssetGroupCirculationPumpStatusControl
                auroraAssetGroup={auroraAssetGroup}
                afterSubmit={(status) =>
                  setCirculationPumpOn(status === AuroraCirculationPumpStatus.ON)
                }
              />

              <AuroraAssetGroupCirculationPumpModeControl auroraAssetGroup={auroraAssetGroup} />
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
